/**
 * Preset global Góndola para PrimeNG en modo `unstyled`.
 *
 * Registrado en `app.config.ts` vía `providePrimeNG({ unstyled: true, pt: GondolaPreset })`.
 * Define el diseño Góndola para TODOS los componentes PrimeNG del proyecto, de modo
 * que los templates usen los componentes directamente, sin `[pt]` por template.
 *
 * Notas de arquitectura (ver CLAUDE.md):
 *  - `p-table`: su diseño vive en `src/styles.css` colgado del tag `<p-table>`, NO aquí.
 *    En modo unstyled el `[class]="cn(cx('table'), tableStyleClass)"` del template borra
 *    lo que ponga el preset, así que el estándar de tablas se aplica por selector de tipo.
 *  - `p-dialog` / `p-confirmDialog`: los elementos `root`, `mask` (y `content` del dialog)
 *    usan `cn(cx('key'), inputProp)`; cuando el input está vacío `cn()` devuelve "" y borra
 *    las clases del preset. Por eso esas clases se pasan como inputs directos en el template
 *    (`styleClass` / `contentStyleClass` / `maskStyleClass`). Aquí solo se estilan las
 *    secciones que el preset sí controla (header, footer, botones, mensaje, icono).
 */

const field =
  'h-11 w-full rounded-lg border border-gray-300 bg-white px-4 text-theme-sm text-gray-800 ' +
  'shadow-theme-xs transition placeholder:text-gray-400 hover:border-gray-400 ' +
  'focus:border-brand-300 focus:outline-none focus:ring-4 focus:ring-brand-500/10 ' +
  'disabled:cursor-not-allowed disabled:bg-gray-50 ' +
  'dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-gray-500 ' +
  'dark:hover:border-gray-600';

const overlayPanel =
  'z-[99001] mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white ' +
  'shadow-theme-lg dark:border-gray-800 dark:bg-gray-900';

const ghostIconButton =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition ' +
  'hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200';

export const GondolaPreset = {
  // --- Select ------------------------------------------------------------
  select: {
    root: 'relative inline-flex w-full cursor-pointer items-center justify-between ' + field + ' px-0',
    label:
      'grow truncate py-2.5 pl-4 pr-2 text-theme-sm text-gray-800 dark:text-white/90 ' +
      'data-[p-placeholder=true]:text-gray-400 dark:data-[p-placeholder=true]:text-gray-500',
    dropdown: 'flex w-10 items-center justify-center text-gray-500 dark:text-gray-400',
    dropdownIcon: 'text-sm',
    clearIcon: 'mr-2 text-sm text-gray-400',
    overlay: overlayPanel,
    header: 'border-b border-gray-100 p-2 dark:border-gray-800',
    pcFilter: {
      root:
        'h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-theme-sm text-gray-800 ' +
        'focus:border-brand-300 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white/90',
    },
    listContainer: 'max-h-60 overflow-auto',
    list: 'flex flex-col gap-0.5 p-2',
    optionGroupLabel: 'px-3 py-1.5 text-theme-xs font-semibold uppercase tracking-wide text-gray-400',
    option:
      'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-theme-sm text-gray-700 transition ' +
      'hover:bg-brand-50 data-[p-focused=true]:bg-brand-50 ' +
      'data-[p-selected=true]:bg-brand-50 data-[p-selected=true]:font-medium data-[p-selected=true]:text-brand-600 ' +
      'dark:text-gray-300 dark:hover:bg-white/5 dark:data-[p-focused=true]:bg-white/5 ' +
      'dark:data-[p-selected=true]:bg-brand-500/10 dark:data-[p-selected=true]:text-brand-400',
    optionLabel: 'grow truncate',
    optionCheckIcon: 'text-brand-500 dark:text-brand-400',
    emptyMessage: 'px-3 py-6 text-center text-theme-sm text-gray-500 dark:text-gray-400',
  },

  // --- DatePicker --------------------------------------------------------
  datepicker: {
    root: 'relative inline-flex w-full',
    pcInputText: { root: field },
    inputIconContainer: 'absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-gray-400',
    panel: overlayPanel + ' w-80 p-4',
    header: 'mb-3 flex items-center justify-between',
    title: 'flex items-center gap-1 text-theme-sm font-semibold text-gray-800 dark:text-white/90',
    pcPrevButton: { root: ghostIconButton + ' h-8 w-8' },
    pcNextButton: { root: ghostIconButton + ' h-8 w-8' },
    selectMonth: 'rounded-md px-2 py-1 font-semibold hover:bg-gray-100 dark:hover:bg-white/5',
    selectYear: 'rounded-md px-2 py-1 font-semibold hover:bg-gray-100 dark:hover:bg-white/5',
    dayView: 'w-full border-collapse',
    weekDay: 'p-1 text-center text-theme-xs font-medium text-gray-400',
    weekDayCell: 'p-0.5',
    buttonbar: 'mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-800',
    pcTodayButton: {
      root: 'rounded-lg px-3 py-1.5 text-theme-sm font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-white/5',
    },
    pcClearButton: {
      root: 'rounded-lg px-3 py-1.5 text-theme-sm font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5',
    },
  },

  // --- InputText / IconField --------------------------------------------
  inputtext: { root: field },
  iconfield: { root: 'relative flex w-full items-center' },
  inputicon: { root: 'pointer-events-none absolute left-3.5 text-gray-400' },

  // --- Button (usado dentro de diálogos; los botones simples van en Tailwind) ---
  button: {
    root:
      'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-2.5 ' +
      'text-theme-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600 ' +
      'focus:outline-none focus:ring-4 focus:ring-brand-500/20 ' +
      'disabled:cursor-not-allowed disabled:opacity-50',
    label: 'font-medium',
    icon: 'text-base',
    loadingIcon: 'animate-spin text-base',
  },

  // --- Dialog (root/mask/content los pone el template, ver CLAUDE.md) ----
  dialog: {
    header: 'flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-800',
    title: 'text-theme-xl font-semibold text-gray-800 dark:text-white/90',
    headerActions: 'flex items-center gap-1',
    pcCloseButton: { root: ghostIconButton },
    footer: 'flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800',
  },

  // --- ConfirmDialog (root/mask los pone el template) -------------------
  confirmdialog: {
    message: 'flex flex-col gap-1 px-6 py-5 text-theme-sm text-gray-600 dark:text-gray-300',
    icon: 'text-2xl text-warning-500',
    pcAcceptButton: {
      root:
        'inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-theme-sm ' +
        'font-medium text-white transition hover:bg-brand-600',
    },
    pcRejectButton: {
      root:
        'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 ' +
        'text-theme-sm font-medium text-gray-700 transition hover:bg-gray-50 ' +
        'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-white/5',
    },
  },

  // --- Toast -------------------------------------------------------------
  toast: {
    root: 'w-96 max-w-[calc(100vw-2rem)]',
    message:
      'mb-3 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-theme-lg ' +
      'dark:border-gray-800 dark:bg-gray-900',
    messageContent: 'flex w-full items-start gap-3',
    messageIcon: 'mt-0.5 text-lg text-brand-500',
    messageText: 'flex-1',
    summary: 'text-theme-sm font-semibold text-gray-800 dark:text-white/90',
    detail: 'mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400',
    closeButton: { root: ghostIconButton + ' ml-auto h-6 w-6' },
  },

  // --- Tooltip -----------------------------------------------------------
  tooltip: {
    root: 'absolute z-[100001] max-w-xs',
    text:
      'rounded-lg bg-gray-900 px-3 py-2 text-theme-xs font-medium text-white shadow-theme-lg ' +
      'dark:bg-gray-700',
  },

  // --- Paginator (dentro de p-table) ------------------------------------
  // OJO: la clave root `paginator` NO va aquí. El host <p-paginator> liga su clase
  // con `cn(cx('paginator'), styleClass)` y en unstyled `cn()` la borra (mismo bug
  // que <p-table>/<p-dialog>), así que el layout del contenedor vive colgado del tag
  // `p-paginator` en `src/styles.css`. Aquí solo las piezas internas, que sí aplican.
  paginator: {
    first: ghostIconButton + ' disabled:opacity-40',
    prev: ghostIconButton + ' disabled:opacity-40',
    next: ghostIconButton + ' disabled:opacity-40',
    last: ghostIconButton + ' disabled:opacity-40',
    pages: 'flex items-center gap-1',
    // Botón de página numerada. El activo se detecta por `aria-current="page"`
    // (atributo que PrimeNG pone en el template), no por la clase `p-paginator-page-selected`,
    // que en modo unstyled el preset reemplaza.
    page:
      'flex h-9 min-w-9 items-center justify-center rounded-lg px-3 text-theme-sm font-medium ' +
      'text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 ' +
      'aria-[current=page]:bg-brand-500 aria-[current=page]:text-white aria-[current=page]:hover:bg-brand-600 ' +
      'dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200 ' +
      'dark:aria-[current=page]:bg-brand-500 dark:aria-[current=page]:text-white',
    current: 'px-3 text-theme-sm text-gray-500 dark:text-gray-400',
  },
};
